import equal from "fast-deep-equal";
import { useEffect, useMemo, useState } from "react";

import {
  getAllowedPlayResults,
  getAutoPlayResult,
} from "../../constants/tagOptions";
import { Activity, GameFullResponse } from "../../types/gameFull";
import {
  NOT_EDITED,
  rebalanceResults,
  ResultsEdited,
  shouldRederiveResults,
  SituationBefore,
  withSituationResults,
} from "../../utils/autoResults";
import {
  getContactTypeFor,
  getEmptyTags,
  getInitialTags,
  getStateBeforePitch,
  getSuggestedContactType,
  getSuggestedPitchResult,
  getSuggestedPitchType,
  PitchTags,
  toBaserunnerFlags,
} from "../../utils/pitchTagging";
import {
  getInitialConfirmed,
  getInitialEdited,
  getInitialSection,
  getNextSection,
  getPreviousSection,
  getSectionToOpen,
  isSectionEnabled,
  Section,
  SECTIONS,
  TagKey,
  updateConfirmed,
  WAITING_SECTIONS,
  withAutoResults,
  withGating,
  withSuggestions,
} from "./sections";

/**
 * How long an arrived pitch without hit data waits for it before the form
 * opens. Measured on live games, most hit data lands within ~300ms of the
 * pitch; the rare later one fills in what hasn't been picked.
 */
export const HIT_DATA_WAIT_MS = 1000;

/**
 * Form state for the Pitch Details panel: the tags, the open section, which
 * tags the scorekeeper confirmed (the rest show "Auto Detected"), and which
 * results they edited by hand. Picks fill in what they imply (play result,
 * results) and open the next section that still waits on a pick.
 */
export const usePitchTagsForm = (
  data: GameFullResponse,
  /** Null while waiting on the pitch */
  activity: Activity | null,
) => {
  const isWaiting = !activity;
  const sections = isWaiting ? WAITING_SECTIONS : SECTIONS;

  const [tags, setTags] = useState<PitchTags>(() =>
    activity
      ? getInitialTags(data, activity)
      : getEmptyTags(data.game.baseRunners),
  );
  /** The open section; Results (always shown) when they're all collapsed */
  const [activeSection, setActiveSection] = useState<Section | null>(() =>
    getInitialSection(tags, activity),
  );
  const [confirmed, setConfirmed] = useState(() =>
    getInitialConfirmed(activity),
  );
  const [resultsEdited, setResultsEdited] = useState<ResultsEdited>(() =>
    getInitialEdited(activity),
  );

  /** Tags filled in by the form, not picked: confirmations drop for them */
  const setFilledTags = (next: PitchTags) => {
    setConfirmed(updateConfirmed(confirmed, tags, next));
    setTags(next);
  };

  const hitData = activity?.pitch.hitData ?? null;
  const suggestedPitchType = getSuggestedPitchType(
    activity?.pitch.pitchData ?? null,
    data.game.sport,
  );
  const suggestedPitchResult = getSuggestedPitchResult(hitData);
  /** Tracker suggestions, labeled on their options */
  const suggestions: Partial<Record<TagKey, string | null>> = {
    pitchType: suggestedPitchType,
    pitchResult: suggestedPitchResult,
    contactType: getSuggestedContactType(hitData),
  };

  // What the form fills in from: outs, runners and the count
  const before = useMemo((): SituationBefore & {
    balls: number;
    strikes: number;
  } => {
    const state =
      (activity && getStateBeforePitch(data, activity)) ?? data.game;
    return {
      baseRunners: toBaserunnerFlags(state.baseRunners),
      outs: state.outs,
      balls: state.balls,
      strikes: state.strikes,
    };
  }, [data, activity]);
  const autoPlayResult = getAutoPlayResult(tags.pitchResult, before);

  // The situation can change with the form open (the live game state was
  // edited, or the game service caught up): redo what was filled in from it,
  // keeping the scorekeeper's own picks and edits. Saved pitches keep their
  // results.
  const [prevBefore, setPrevBefore] = useState(before);
  const isSituationChanged = !equal(prevBefore, before);
  if (isSituationChanged) {
    setPrevBefore(before);
    if (!activity?.result) {
      const wasAutoPlayResult =
        tags.playResult === getAutoPlayResult(tags.pitchResult, prevBefore);
      // Play result can't be picked while waiting; it's filled in when the
      // pitch arrives
      if (
        !isWaiting &&
        wasAutoPlayResult &&
        tags.playResult !== autoPlayResult
      ) {
        setFilledTags(
          withAutoResults({ ...tags, playResult: autoPlayResult }, before),
        );
        setResultsEdited(NOT_EDITED);
      } else {
        setTags(withSituationResults(tags, resultsEdited, before));
      }
    }
  }

  /** `base` with every unconfirmed tag filled from the current suggestions */
  const fillSuggestions = (base: PitchTags) => {
    const filled = withSuggestions(
      base,
      confirmed,
      { pitchType: suggestedPitchType, pitchResult: suggestedPitchResult },
      hitData,
      (pitchResult) => getAutoPlayResult(pitchResult, before),
    );
    if (!shouldRederiveResults(base, filled)) return filled;
    setResultsEdited(NOT_EDITED);
    return withAutoResults(filled, before);
  };

  const applySuggestions = (opensNextSection: boolean) => {
    const filled = fillSuggestions(tags);
    setFilledTags(filled);
    if (opensNextSection) setActiveSection(getSectionToOpen(filled));
  };

  // A pitch often arrives a moment before its hit data. Without it, the body
  // holds its loading state up to HIT_DATA_WAIT_MS so a late fair/foul call
  // doesn't snap into a form already open. A pick ends the hold early.
  const [hold, setHold] = useState<"holding" | "expired" | null>(null);
  const suggestionsKey = [
    suggestedPitchType,
    suggestedPitchResult,
    suggestions.contactType,
  ].join("|");
  const [prevSuggestionsKey, setPrevSuggestionsKey] = useState(suggestionsKey);
  const [prevIsWaiting, setPrevIsWaiting] = useState(isWaiting);

  if (isSituationChanged) {
    // Its tag updates land first; React re-renders right away with them, and
    // the checks below run then
  } else if (prevIsWaiting !== isWaiting) {
    setPrevIsWaiting(isWaiting);
    setPrevSuggestionsKey(suggestionsKey);
    if (!isWaiting) {
      if (hitData) applySuggestions(true);
      else setHold("holding");
    }
  } else if (hold && (hitData || hold === "expired")) {
    setHold(null);
    setPrevSuggestionsKey(suggestionsKey);
    applySuggestions(true);
  } else if (prevSuggestionsKey !== suggestionsKey) {
    // Hit data that comes after the hold fills what hasn't been picked. The
    // open section only moves on if it was open for want of a value
    setPrevSuggestionsKey(suggestionsKey);
    if (!isWaiting && !hold && !activity?.result) {
      const wasOpenForValue = activeSection === getSectionToOpen(tags);
      const filled = fillSuggestions(tags);
      setFilledTags(filled);
      if (wasOpenForValue) setActiveSection(getSectionToOpen(filled));
    }
  }

  useEffect(() => {
    if (hold !== "holding") return;
    const timer = setTimeout(() => setHold("expired"), HIT_DATA_WAIT_MS);
    return () => clearTimeout(timer);
  }, [hold]);

  const allowedPlayResults = getAllowedPlayResults(tags.pitchResult);

  const isOptionDisabled = (section: Section, value: string) => {
    if (section === "playResult") return !allowedPlayResults.includes(value);
    if (section === "contactType") return !isSectionEnabled(section, tags);
    return false;
  };

  /** Filled in by the tracker or a rule, and not yet picked or confirmed */
  const isAutoDetected = (section: TagKey) =>
    tags[section] !== null && !confirmed.has(section);

  const select = (section: TagKey, value: string) => {
    // A pick during the hold ends it: the suggestions so far fill the rest
    const base = hold ? fillSuggestions(tags) : tags;
    setHold(null);
    let nextTags = withGating({
      ...base,
      // Picking the current value again confirms it. Outcome of the play can
      // be left blank, so there it clears instead
      [section]:
        section === "playResult" && base[section] === value ? null : value,
    });

    // A new pitch result can pre-select the play result and contact type
    if (section === "pitchResult") {
      nextTags = {
        ...nextTags,
        playResult:
          nextTags.playResult ??
          getAutoPlayResult(nextTags.pitchResult, before),
        contactType:
          nextTags.contactType ??
          getContactTypeFor(nextTags.pitchResult, hitData),
      };
    }

    if (shouldRederiveResults(base, nextTags)) {
      nextTags = withAutoResults(nextTags, before);
      setResultsEdited(NOT_EDITED);
    }
    setConfirmed(updateConfirmed(confirmed, tags, nextTags, section));
    setTags(nextTags);
    setActiveSection(getSectionToOpen(nextTags, isWaiting));
  };

  /** Moving to a section during the hold ends it, filling what it can. */
  const endHold = () => {
    if (!hold) return;
    setHold(null);
    applySuggestions(false);
  };

  /** Opens a collapsed section, or collapses the open one. */
  const toggleSection = (section: TagKey) => {
    endHold();
    if (activeSection !== section) {
      setActiveSection(section);
    } else {
      setActiveSection(sections.includes("results") ? "results" : null);
    }
  };

  /** Applies a hand edit and rebalances the results the user hasn't touched. */
  const changeResult = <K extends keyof ResultsEdited>(
    field: K,
    value: PitchTags[K],
  ) => {
    const nextEdited = { ...resultsEdited, [field]: true };
    const next = { ...tags, [field]: value };
    setTags({
      ...next,
      ...rebalanceResults(next, nextEdited, next, before),
    });
    setResultsEdited(nextEdited);
  };

  /** Back to the pitch's saved tags (or the suggestion), top of the form. */
  const reset = (pitch: Activity) => {
    const initialTags = getInitialTags(data, pitch);
    setHold(null);
    setTags(initialTags);
    setConfirmed(getInitialConfirmed(pitch));
    setResultsEdited(getInitialEdited(pitch));
    setActiveSection(getInitialSection(initialTags, pitch));
  };

  return {
    tags,
    resultsEdited,
    activeSection,
    /** Briefly waiting on the arrived pitch's hit data: the body keeps loading */
    isHolding: hold !== null,
    suggestions,
    isOptionDisabled,
    isAutoDetected,
    select,
    toggleSection,
    changeResult,
    reset,
    goToNextSection: () => {
      endHold();
      setActiveSection(getNextSection(activeSection, tags, sections));
    },
    goToPreviousSection: () => {
      endHold();
      setActiveSection(getPreviousSection(activeSection, tags, sections));
    },
  };
};
